/**
 * The only place a `MusRoom` is joined (frontend.md §1.3).
 *
 * The room is joined once, in `Room.tsx`, and lives for the whole session —
 * pre-game seating and the live table are one route precisely so that pressing
 * Start does not unmount the tree and tear the connection down between the
 * leader's click and the first deal (§1.1).
 *
 * The reconnection token in `sessionStorage` buys F5-survival for free: within
 * the tier's grace window a hard refresh rejoins the same seat with the same
 * hand instead of handing it to a bot.
 *
 * **The connection outlives the effect.** React 18 StrictMode mounts, unmounts
 * and remounts every effect in development, and a websocket cannot be treated
 * like a subscription: leaving is a message to the server, `MusRoom` is
 * `autoDispose`, and the creator is its only occupant — so a naive
 * cleanup-then-reconnect disposed the room the user had just made and the
 * remount failed with "room not found". Not a development-only concern either: a
 * quick back-then-forward does the same thing. So the room lives in a
 * module-level holder, cleanup only *schedules* a close, and an effect
 * re-entering for the same room cancels that and adopts what is already open.
 */

import type { Room } from 'colyseus.js';
import { useEffect, useState } from 'react';

import { director } from '../game/anim/director';
import { useStore } from '../store';

import { bindRoom } from './bindRoom';
import { colyseus } from './colyseus';
import { setActiveRoom } from './send';
import { takeCreatedRoom } from './useLobby';

/** Long enough to span a StrictMode remount, short enough to feel immediate. */
const CLOSE_GRACE_MS = 400;

function tokenKey(roomId: string): string {
  return `ordago.rt:${roomId}`;
}

export function storeReconnectionToken(roomId: string, token: string): void {
  try {
    sessionStorage.setItem(tokenKey(roomId), token);
  } catch {
    // Private-mode Safari throws on write. Losing F5-survival is survivable.
  }
}

export function readReconnectionToken(roomId: string): string | null {
  try {
    return sessionStorage.getItem(tokenKey(roomId));
  } catch {
    return null;
  }
}

export function clearReconnectionToken(roomId: string): void {
  try {
    sessionStorage.removeItem(tokenKey(roomId));
  } catch {
    /* see above */
  }
}

// ─────────────────────────────────────────────────────────── the live connection

interface Held {
  roomId: string;
  room: Room<unknown>;
  unbind: () => void;
}

let held: Held | null = null;
let pendingConnect: { roomId: string; promise: Promise<Room<unknown>> } | null = null;
let closeTimer: ReturnType<typeof setTimeout> | undefined;

function teardown(): void {
  const current = held;
  held = null;
  setActiveRoom(null);
  director.clear();
  if (current !== null) {
    current.unbind();
    void current.room.leave();
  }
  useStore.getState().room.reset();
  useStore.getState().table.reset();
}

async function connect(roomId: string, token: string): Promise<Room<unknown>> {
  // The creator arrives here already connected: `client.create()` joined them.
  // Adopting that connection avoids a second one, and avoids the lobby-phase
  // `onLeave` freeing the seat they just took.
  let room = takeCreatedRoom(roomId) ?? undefined;

  if (room === undefined) {
    const saved = readReconnectionToken(roomId);
    if (saved !== null) {
      // A stale token is the normal case after the grace window, so a failure
      // here is not an error — it just means joining fresh.
      room = await colyseus()
        .reconnect(saved)
        .catch(() => undefined);
      if (room === undefined) clearReconnectionToken(roomId);
    }
  }

  room ??= await colyseus().joinById(roomId, { token });

  storeReconnectionToken(roomId, room.reconnectionToken);
  held = { roomId, room, unbind: bindRoom(room) };
  setActiveRoom(room);
  return room;
}

export interface JoinState {
  room: Room<unknown> | null;
  error: string | null;
}

export function useJoinRoom(roomId: string | undefined): JoinState {
  const token = useStore((s) => s.auth.accessToken);
  const [state, setState] = useState<JoinState>({ room: null, error: null });

  useEffect(() => {
    if (roomId === undefined || token === null) return;

    // A remount for the same room cancels the close its own unmount scheduled.
    clearTimeout(closeTimer);
    closeTimer = undefined;

    let cancelled = false;

    if (held !== null && held.roomId === roomId) {
      setState({ room: held.room, error: null });
      useStore.setState((s) => ({ room: { ...s.room, connection: 'open', joinError: null } }));
    } else {
      if (held !== null) teardown();

      useStore.setState((s) => ({
        room: { ...s.room, roomId, connection: 'connecting', joinError: null },
      }));

      // Two effects racing the same join would open two sockets and cost the
      // player their seat; the second one waits on the first one's promise.
      pendingConnect =
        pendingConnect?.roomId === roomId
          ? pendingConnect
          : { roomId, promise: connect(roomId, token) };

      void pendingConnect.promise
        .then((room) => {
          if (cancelled) return;
          setState({ room, error: null });
          useStore.setState((s) => ({ room: { ...s.room, connection: 'open', joinError: null } }));
        })
        .catch((cause: unknown) => {
          const message = cause instanceof Error ? cause.message : 'JOIN_FAILED';
          if (cancelled) return;
          setState({ room: null, error: message });
          useStore.setState((s) => ({
            room: { ...s.room, connection: 'closed', joinError: message },
          }));
        })
        .finally(() => {
          if (pendingConnect?.roomId === roomId) pendingConnect = null;
        });
    }

    return () => {
      cancelled = true;
      closeTimer = setTimeout(() => {
        closeTimer = undefined;
        teardown();
      }, CLOSE_GRACE_MS);
    };
  }, [roomId, token]);

  return state;
}

/**
 * The live room, for the debug hook only. Everything in the app reads the store;
 * this exists so a test — or a developer chasing a projection bug — can compare
 * the mirror against the schema it was built from.
 */
export function heldRoom(): Room<unknown> | null {
  return held?.room ?? null;
}

/** Leaves at once and forgets the seat, for the "leave the table" button. */
export function leaveRoomNow(): void {
  clearTimeout(closeTimer);
  closeTimer = undefined;
  const roomId = held?.roomId;
  if (roomId !== undefined) clearReconnectionToken(roomId);
  teardown();
}
